import { Alert, Grid, Paper, Stack, Typography } from "@mui/material";
import { CreditCheckField } from "./CreditCheckField";
import { CreditScoreDonut } from "./CreditScoreDonut";
import { ValidationErrors } from "./ValidationErrors";
import { formatCurrency } from "@poseidon/utils";
import Divider from "@mui/material/Divider";
import {
  SummaryTable,
  SummaryTableRow,
} from "@poseidon/ui/components";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";

function formatCount(value) {
  if (value === -1) {
    return "No Trace";
  }
  if (value === 0) {
    return "None";
  }
  if (value >= 9) {
    return "9+";
  }
  return value;
}

function formatNoTrace(value, suffix) {
  if (value === -1) {
    return "No Trace";
  }
  if (suffix) {
    return `${value} ${suffix}`;
  }
  return value;
}
function formatNoTraceCurrency(value) {
  if (value === -1) {
    return "No Trace";
  }
  return formatCurrency(value);
}

function CreditCheckTopFields({ mapped }) {
  return (
    <>
      <Grid item md={4} sm={6} xs={12}>
        <CreditCheckField
          label="Time on ER"
          value={formatNoTrace(mapped.timeOnElectoralRoleInYears, "years")}
        />
      </Grid>
      <Grid item md={4} sm={6} xs={12}>
        <CreditCheckField
          label="Number of Searches Last 12m"
          value={formatCount(mapped.numberOfSearchesInLast12Months)}
        />
      </Grid>
      <Grid item md={4} sm={6} xs={12}>
        <CreditCheckField
          label="CIFAS?"
          value={mapped.cifasDetected ? "Yes" : "No"}
        />
      </Grid>
      <Grid item md={4} sm={6} xs={12}>
        <CreditCheckField
          label="Total Outstanding Debt"
          value={formatNoTraceCurrency(mapped.totalOutstandingActiveDebt)}
        />
      </Grid>
      <Grid item md={4} sm={6} xs={12}>
        <CreditCheckField
          label="Debt Trend"
          value={formatNoTrace(mapped.debtTrend)}
        />
      </Grid>
      <Grid item md={4} sm={6} xs={12}>
        <CreditCheckField
          label="Number of Settled (sat.)"
          value={formatNoTrace(mapped.numberOfAccountsSettledSatisfactorily)}
        />
      </Grid>
    </>
  );
}
function CreditCheckBottomFields({ mapped }) {
  return (
    <>
      <Grid item md={4} sm={6} xs={12}>
        <CreditCheckField
          label="Number of Defaulted Accs"
          value={formatNoTrace(mapped.numberOfDefaultedAccounts)}
        />
        <CreditCheckField
          label="Defaulted Balance"
          value={formatNoTraceCurrency(mapped.defaultedBalance)}
        />
      </Grid>
      <Grid item md={4} sm={6} xs={12}>
        <CreditCheckField
          label="Number of Accs in Arrears"
          value={formatNoTrace(mapped.numberOfAccountsInArrears)}
        />
        <CreditCheckField
          label="Total Arrears"
          value={formatNoTraceCurrency(mapped.totalArrears)}
        />
      </Grid>
      <Grid item md={4} sm={6} xs={12}>
        <CreditCheckField
          label="CCJ/ Bankruptcy?"
          value={mapped.hasCcjOrBankruptcy ? "Yes" : "No"}
        />
      </Grid>
    </>
  );
}
function CreditCheckMortgageFields({ mapped }) {
  return (
    <Grid item md={4} sm={6} xs={12}>
      <Paper variant="outlined" sx={{ my: 1, p: 1 }}>
        <Stack textAlign="left" spacing={1}>
          <Typography variant="subtitle2">Mortgages</Typography>
          <SummaryTable>
            <SummaryTableRow label="Balance">
              {formatNoTraceCurrency(mapped.mortgageBalance)}
            </SummaryTableRow>
            <SummaryTableRow label="Monthly Payments">
              {formatNoTraceCurrency(mapped.monthlyMortgagePayments)}
            </SummaryTableRow>
            <SummaryTableRow label="Missed Payments">
              {formatNoTrace(mapped.missedMortgagePayments)}
            </SummaryTableRow>
          </SummaryTable>
        </Stack>
      </Paper>
    </Grid>
  );
}
function CreditCheckHpFields({ mapped }) {
  return (
    <Grid item md={4} sm={6} xs={12}>
      <Paper variant="outlined" sx={{ my: 1, p: 1 }}>
        <Stack textAlign="left" spacing={1}>
          <Typography variant="subtitle2">HP/Loans</Typography>
          <SummaryTable>
            <SummaryTableRow label="Balance">
              {formatNoTraceCurrency(mapped.hpLoanBalance)}
            </SummaryTableRow>
          </SummaryTable>
        </Stack>
      </Paper>
    </Grid>
  );
}
function CreditCheckCreditCardFields({ mapped }) {
  return (
    <Grid item md={4} sm={6} xs={12}>
      <Paper variant="outlined" sx={{ my: 1, p: 1 }}>
        <Stack textAlign="left" spacing={1}>
          <Typography variant="subtitle2">Credit Cards</Typography>
          <SummaryTable>
            <SummaryTableRow label="Balance">
              {formatNoTraceCurrency(mapped.creditCardBalance)}
            </SummaryTableRow>
            <SummaryTableRow label="Limits">
              {formatNoTraceCurrency(mapped.creditCardLimit)}
            </SummaryTableRow>
            <SummaryTableRow label="Usage">
              {mapped.creditCardUsage}%
            </SummaryTableRow>
            <SummaryTableRow label="PDL Balance">
              {formatNoTraceCurrency(mapped.pdlAccountBalance)}
            </SummaryTableRow>
          </SummaryTable>
        </Stack>
      </Paper>
    </Grid>
  );
}

function CreditCheckTotalDefaultsFields({ mapped }) {
  return (
    <Grid item sm={6} xs={12}>
      <Paper variant="outlined" sx={{ my: 1, p: 1 }}>
        <Stack textAlign="left" spacing={1}>
          <Typography variant="subtitle2">Total Defaults</Typography>
          <SummaryTable>
            <SummaryTableRow label="Last 12m">
              {formatNoTraceCurrency(mapped.totalDefaults12Months)}
            </SummaryTableRow>
            <SummaryTableRow label="Last 72m">
              {formatNoTraceCurrency(mapped.totalDefaults72Months)}
            </SummaryTableRow>
          </SummaryTable>
        </Stack>
      </Paper>
    </Grid>
  );
}

function CreditCheckTotalMissedPaymentsFields({ mapped }) {
  return (
    <Grid item sm={6} xs={12}>
      <Paper variant="outlined" sx={{ my: 1, p: 1 }}>
        <Stack textAlign="left" spacing={1}>
          <Typography variant="subtitle2">Total Missed Payments</Typography>
          <SummaryTable>
            <SummaryTableRow label="Last 12m">
              {formatNoTraceCurrency(mapped.totalMissedPayments12Months)}
            </SummaryTableRow>
            <SummaryTableRow label="Last 72m">
              {formatNoTraceCurrency(mapped.totalMissedPayments72Months)}
            </SummaryTableRow>
          </SummaryTable>
        </Stack>
      </Paper>
    </Grid>
  );
}
function summariseAccounts(accounts) {
  const keys = ["currency", "paymentFrequency", "accountType", "paymentStatus"];
  const sum = [
    "currentBalance",
    "creditLimitBalance",
    "paymentAmount",
    "defaultBalance",
    "missedPayments.seventyTwoMonths",
    "missedPayments.twelveMonths",
  ];
  const grouped = accounts.reduce((agg, account) => {
    const matchedIndex = agg.findIndex((candidate) =>
      keys.every((key) => candidate[key] === account[key])
    );
    const rest = agg.filter((v, idx) => idx !== matchedIndex);

    const currentGroup = agg[matchedIndex] ?? {
      id: keys.map((key) => account[key]).join("-"),
      ...keys.reduce((agg, key) => ({ ...agg, [key]: account[key] }), {}),
      ...sum.reduce((agg, key) => ({ ...agg, [key]: 0 }), {}),
      count: 0,
    };
    const updatedGroup = {
      ...currentGroup,
      ...sum.reduce(
        (agg, key) => ({
          ...agg,
          [key]: (account[key] ?? 0) + currentGroup[key],
          count: currentGroup.count + 1,
        }),
        {}
      ),
    };

    return [...rest, updatedGroup];
  }, []);
  return grouped.sort((a, b) => {
    for (const key of keys) {
      if (a[key] !== b[key]) {
        if (typeof a[key] === "string") {
          return a[key].localeCompare(b[key]);
        }
        if (typeof a[key] === "number") {
          return (a[key] = b[key]);
        }
      }
    }
    return 0;
  });
}
function CreditCheckAccountsSummary({ creditSearch }) {
  const summaries = summariseAccounts(creditSearch?.raw?.creditAccounts ?? []);
  // const
  return (
    <Grid item xs={12}>
      <Paper variant="outlined" sx={{ my: 1, p: 1 }}>
        <Stack textAlign="left" spacing={1}>
          <Typography variant="subtitle2">Accounts Summary</Typography>
          {summaries.length ? (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell component="th">Type</TableCell>
                  <TableCell component="th">Status</TableCell>
                  <TableCell component="th">No</TableCell>
                  <TableCell component="th">Balance</TableCell>
                  <TableCell component="th">Credit limit</TableCell>
                  <TableCell component="th">Payment</TableCell>
                  <TableCell component="th">Default balance</TableCell>
                  <TableCell component="th">Missed (12mo)</TableCell>
                  <TableCell component="th">Missed (72mo)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {summaries.map((summary) => (
                  <TableRow key={summary.id}>
                    <TableCell>
                      {summary.accountType} ({summary.currency}:{" "}
                      {summary.paymentFrequency})
                    </TableCell>
                    <TableCell>{summary.paymentStatus}</TableCell>
                    <TableCell>{summary.count}</TableCell>
                    <TableCell>
                      {formatCurrency(summary.currentBalance)}
                    </TableCell>
                    <TableCell>
                      {formatCurrency(summary.creditLimitBalance)}
                    </TableCell>
                    <TableCell>
                      {formatCurrency(summary.paymentAmount)}
                    </TableCell>
                    <TableCell>
                      {formatCurrency(summary.defaultBalance)}
                    </TableCell>
                    <TableCell>
                      {summary["missedPayments.twelveMonths"]}
                    </TableCell>
                    <TableCell>
                      {summary["missedPayments.seventyTwoMonths"]}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Typography variant="caption">No accounts found</Typography>
          )}
        </Stack>
      </Paper>
    </Grid>
  );
}

export const CreditCheck = ({ creditCheck }) => {
  // requesting, requested, fetched, error
  if (creditCheck?.status === "error") {
    return <ValidationErrors errors={creditCheck.error} />;
  }

  if (creditCheck.status !== "fetched") {
    return (
      <Stack spacing={3}>
        <Alert severity="info" sx={{ my: 2 }}>
          Credit check status: {creditCheck.status}
        </Alert>
      </Stack>
    );
  }

  return (
    <Stack>
      <Grid container spacing={2} mt={0}>
        <Grid item xs={12} sm={1} md={2}></Grid>
        <Grid
          item
          xs={12}
          sm={5}
          md={4}
          justifyContent="center"
          alignItems="center"
        >
          <CreditScoreDonut
            title="Credit Score"
            value={creditCheck?.creditSearch?.mapped.creditScore}
          />
        </Grid>
        <Grid
          item
          xs={12}
          sm={5}
          md={4}
          justifyContent="center"
          alignItems="center"
        >
          <CreditScoreDonut
            title="Credit Score (with Associates)"
            value={creditCheck?.creditSearch?.mapped.creditScoreWithAssociates}
          />
        </Grid>
        <Grid item xs={12} sm={1} md={2}></Grid>
        <CreditCheckTopFields mapped={creditCheck.creditSearch.mapped} />
        <Grid item xs={12}>
          <Divider />
        </Grid>
        <CreditCheckMortgageFields mapped={creditCheck.creditSearch.mapped} />
        <CreditCheckHpFields mapped={creditCheck.creditSearch.mapped} />
        <CreditCheckCreditCardFields mapped={creditCheck.creditSearch.mapped} />
        <Grid item xs={12}>
          <Divider />
        </Grid>
        <CreditCheckAccountsSummary creditSearch={creditCheck.creditSearch} />
        <Grid item xs={12}>
          <Divider />
        </Grid>

        <CreditCheckTotalDefaultsFields
          mapped={creditCheck.creditSearch.mapped}
        />
        <CreditCheckTotalMissedPaymentsFields
          mapped={creditCheck.creditSearch.mapped}
        />

        <Grid item xs={12}>
          <Divider />
        </Grid>
        <CreditCheckBottomFields mapped={creditCheck.creditSearch.mapped} />
      </Grid>
    </Stack>
  );
};
